import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { LocalizationService } from '../../services/localization.service';
import { FilterUsers } from '../action/management.action';
import { AccountInfo } from './../../models/accountInfo.model';
import { LocalCacheModel } from './../../models/local-cache.model';
import { LocalStorageKeys } from './../../models/local-storage-keys.model';
import { UserModel } from './../../models/user.model';
import { LocalCacheService } from './../../services/local-cache.service';
import { NotificationService } from './../../services/notification.service';
import { UserService } from './../../services/user.service';
import {
  ChangePassword,
  GetAccountFromCache,
  IsAuthorized,
  RegisterUser,
  SelectAccount,
  SetAccount,
  SetTitle,
  UpdateAccount,
} from './../action/account.action';

export interface AccountStateModel {
  isAuthorized: boolean;
  accountInfo: AccountInfo;
  pageTitle: string;
  isRegistered: boolean;
  selectedAccount: UserModel;
}

@State<AccountStateModel>({
  name: 'accountstate',
  defaults: {
    isAuthorized: false,
    pageTitle: 'Building Management',
    accountInfo: undefined,
    isRegistered: false,
    selectedAccount: new UserModel(),
  },
})
@Injectable()
export class AccountState {
  constructor(
    private userService: UserService,
    private notificationService: NotificationService,
    private localCacheService: LocalCacheService,
    private localizationService: LocalizationService
  ) {}

  @Selector()
  static pageTitle(ctx: AccountStateModel) {
    return ctx.pageTitle;
  }

  @Selector()
  static isAuthorized(ctx: AccountStateModel) {
    return ctx.isAuthorized;
  }
  @Selector()
  static isRegistered(ctx: AccountStateModel) {
    return ctx.isRegistered;
  }

  @Selector()
  static account(ctx: AccountStateModel) {
    return ctx.accountInfo;
  }

  @Selector()
  static selectedAccount(ctx: AccountStateModel) {
    return ctx.selectedAccount;
  }

  @Action(IsAuthorized)
  async isAuthorized(
    ctx: StateContext<AccountStateModel>,
    action: IsAuthorized
  ) {
    ctx.patchState({
      isAuthorized: action.isAuthorized,
      accountInfo: undefined,
    });
  }

  @Action(SetTitle)
  async setTitle(ctx: StateContext<AccountStateModel>, action: SetTitle) {
    ctx.patchState({
      pageTitle: action.title,
    });
  }

  @Action(ChangePassword)
  async changePassword(
    ctx: StateContext<AccountStateModel>,
    action: ChangePassword
  ) {
    var data = await this.userService.changePassword(action.model).toPromise();
    if (data) {
      this.notificationService.info(
        this.localizationService.translate('change-password'),
        this.localizationService.translate('successfull-change-password')
      );
    } else {
      this.notificationService.warning(
        this.localizationService.translate('change-password'),
        this.localizationService.translate('successfull-change-password')
      );
    }
  }

  @Action(RegisterUser)
  async registerUser(
    ctx: StateContext<AccountStateModel>,
    action: RegisterUser
  ) {
    const data = await this.userService.register(action.model).toPromise();

    if (data) {
      if (data.status === 'Success') {
        this.notificationService.info(data.status, data.message);
        ctx.patchState({
          isRegistered: true,
        });

        ctx.dispatch(new FilterUsers());
      } else {
        this.notificationService.warning(data.status, data.message);
      }
    }
  }

  @Action(GetAccountFromCache)
  async getAccountFromCache(
    ctx: StateContext<AccountStateModel>,
    action: GetAccountFromCache
  ) {
    const accountCache = this.localCacheService.get(
      LocalStorageKeys.AccountKey
    );

    if (accountCache && accountCache.value?.length > 0) {
      ctx.patchState({
        accountInfo: JSON.parse(accountCache.value),
      });
    }
  }

  @Action(SetAccount)
  async setUser(ctx: StateContext<AccountStateModel>, action: SetAccount) {
    const accountInfo = new AccountInfo();
    accountInfo.email = action.model.email;
    accountInfo.firstName = action.model.firstname;
    accountInfo.lastName = action.model.lastname;
    accountInfo.userType = action.model.type;
    accountInfo.categories = action.model.categories;

    const model = new LocalCacheModel(
      LocalStorageKeys.AccountKey,
      JSON.stringify(accountInfo)
    );
    this.localCacheService.set(model);

    ctx.patchState({
      accountInfo: accountInfo,
    });
  }

  @Action(UpdateAccount)
  async updateAccount(
    ctx: StateContext<AccountStateModel>,
    action: UpdateAccount
  ) {
    const accountInfo = new UserModel();
    accountInfo.id = action.model.id;
    accountInfo.email = action.model.email;
    accountInfo.firstname = action.model.firstname;
    accountInfo.lastname = action.model.lastname;
    accountInfo.type = action.model.type;
    accountInfo.categories = action.model.categories;
    accountInfo.municipalities = action.model.municipalities;

    const data = await this.userService.update(accountInfo).toPromise();

    if (data) {
      this.notificationService.info(
        this.localizationService.translate('update'),
        this.localizationService.translate('successfull-update')
      );
      ctx.dispatch(new FilterUsers());

      ctx.patchState({
        isRegistered: true,
      });
    }
  }

  @Action(SelectAccount)
  async selectAccount(
    ctx: StateContext<AccountStateModel>,
    action: SelectAccount
  ) {
    const account = await this.userService.get(action.id).toPromise();

    if (account) {
      ctx.patchState({
        selectedAccount: account,
      });
    }
  }
}
